'use client';

/**
 * SignupFormsConsolidates — pain-then-product beat after the hero.
 *
 * The hero claims "sign, pay and follow up in one flow." This section
 * shows the cost of NOT having that: most teams stitch Typeform +
 * Stripe Checkout + DocuSign + Mailchimp + a Zap to get the same
 * outcome. Reuses the shared ReplaceTable so the visual pattern
 * stays consistent with /features and the home "What SUM replaces"
 * section.
 */

import Link from 'next/link';
import React from 'react';
import { Locale } from '../../../../i18n-config';
import { useTranslation } from '@/i18n/client';
import ReplaceTable, { ReplaceRow } from '../_shared/sections/ReplaceTable';

type Props = { lang: Locale };

type RowKey = 'capture' | 'payment' | 'esign' | 'booking' | 'followup' | 'profile';

type RowMeta = { key: RowKey; flagship?: boolean; badge?: string; href?: string };

const ROW_ORDER: RowMeta[] = [
  { key: 'capture' },
  { key: 'payment', flagship: true, badge: 'IN-FLOW' },
  { key: 'esign', flagship: true, badge: 'AUTO' },
  { key: 'booking', href: '/online-booking' },
  { key: 'followup' },
  { key: 'profile' },
];

const SignupFormsConsolidates = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const rowCopy = (key: RowKey) => ({
    cat: t(`SignupForms/Consolidates/rows/${key}/cat`),
    tools: t(`SignupForms/Consolidates/rows/${key}/tools`),
  });

  const featureFor = (meta: RowMeta) => {
    const text = t(`SignupForms/Consolidates/rows/${meta.key}/feature`);
    if (!meta.href) return text;
    return (
      <>
        {text}{' '}
        <Link href={`/${lang}${meta.href}`} style={{ color: '#22D3EE' }}>
          {t(`SignupForms/Consolidates/rows/${meta.key}/link`)}
          {'\u00a0→'}
        </Link>
      </>
    );
  };

  const rows: ReplaceRow[] = ROW_ORDER.map((meta) => ({
    key: meta.key,
    flagship: meta.flagship,
    badge: meta.badge,
    ...rowCopy(meta.key),
    feature: featureFor(meta),
  }));

  return (
    <ReplaceTable
      id="what-it-replaces"
      num="01"
      label={t('SignupForms/Consolidates/badge')}
      headlineA={t('SignupForms/Consolidates/title1')}
      headlineB={t('SignupForms/Consolidates/title2')}
      subhead={t('SignupForms/Consolidates/subtitle')}
      rows={rows}
      includedLabel={t('SignupForms/Consolidates/included')}
    />
  );
};

export default SignupFormsConsolidates;
